import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  Input,
  ModalHeader,
} from '@chakra-ui/react';
import { targetTiersApi } from '@texas/api/endpoints/targetTiers/targetTiersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { articleManageEvents } from '../events';
import { request } from '@texas/utils/helpers/httpHelpers';

interface Props extends SharedDisclosureProps {
  tierId: number;
  quantity: number;
}

interface FormValues {
  quantity: number;
}

export function EditQuantityModal({
  tierId,
  quantity,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: { quantity },
  });

  useEffect(() => {
    reset({ quantity });
  }, [quantity, reset]);

  const {
    request: updateTierRequest,
    loading: updating,
    error: updatingError,
  } = useApiRequest(targetTiersApi.updateTier);

  const submit = async (data: FormValues) => {
    await request(updateTierRequest, [tierId, data.quantity], (_) => {
      articleManageEvents.tierUpdated.dispatch();
      reset();
      onClose();
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('tiers.edit')}</ModalHeader>
        {updatingError && <ErrorDetails error={updatingError} />}
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <FormControl isRequired={true} isInvalid={!!errors.quantity}>
              <TexasFormLabel>{t('general.quantity')}</TexasFormLabel>
              <Input
                variant="texas"
                {...register('quantity', {
                  required: true,
                  min: 1,
                })}
                placeholder="Quantity"
                type="number"
                autoFocus={true}
              />
              <ErrorLabel text={errors.quantity?.message} />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <SubmitButton loading={updating} disabled={updating}>
              {t('general.update')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
